import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  slug: '', title: '', h1: '', meta_description: '', eyebrow: '', intro: '',
  sections: [], faqs: [], keywords: [], filter_tags: [],
  featured_collection_ids: [], featured_film_ids: [],
  city: '', culture: '', service: 'photographer', hero_image_url: '', cta_text: 'Check Your Date', is_published: true,
};

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black';
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const ListEditor = ({ items, onChange, fields, addLabel, testid }) => (
  <div className="space-y-3">
    {items.map((it, i) => (
      <div key={i} className="p-3 border border-gray-200 rounded space-y-2 bg-gray-50">
        {fields.map(([key, label, multiline]) => (
          multiline ? (
            <textarea key={key} rows={3} placeholder={label} value={it[key] || ''} className={inputCls}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)))} />
          ) : (
            <input key={key} placeholder={label} value={it[key] || ''} className={inputCls}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)))} />
          )
        ))}
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-xs text-red-600">Remove</button>
      </div>
    ))}
    <button type="button" data-testid={testid} onClick={() => onChange([...items, Object.fromEntries(fields.map(([k]) => [k, '']))])} className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">{addLabel}</button>
  </div>
);

const toList = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

const SeoPagesPanel = () => {
  const [pages, setPages] = useState([]);
  const [collections, setCollections] = useState([]);
  const [films, setFilms] = useState([]);
  const [current, setCurrent] = useState(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    try {
      const [p, c, f] = await Promise.all([
        api.request('/api/admin/seo-pages'),
        fetch(`${API_URL}/api/content/collections`).then((r) => r.json()),
        fetch(`${API_URL}/api/content/film-videos`).then((r) => r.json()),
      ]);
      setPages(p); setCollections(c || []); setFilms(f || []);
    } catch (e) { setMessage('Failed to load: ' + e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (current.id) await api.request(`/api/admin/seo-pages/${current.id}`, { method: 'PUT', body: JSON.stringify(current) });
      else await api.request('/api/admin/seo-pages', { method: 'POST', body: JSON.stringify(current) });
      setMessage('Page saved'); setCurrent(null); load();
    } catch (e) { setMessage('Failed to save: ' + e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this SEO page?')) return;
    await api.request(`/api/admin/seo-pages/${id}`, { method: 'DELETE' });
    load();
  };

  const seed = async () => {
    const r = await api.request('/api/admin/seo-pages/seed-defaults', { method: 'POST' });
    setMessage(`Seeded ${r.created} default page(s)`); load();
  };

  const toggleId = (key, id) => setCurrent((c) => ({
    ...c, [key]: c[key].includes(id) ? c[key].filter((x) => x !== id) : [...c[key], id],
  }));

  if (current) {
    const set = (k, v) => setCurrent((c) => ({ ...c, [k]: v }));
    return (
      <div className="space-y-6" data-testid="seo-page-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{current.id ? 'Edit' : 'New'} SEO Page</h2>
          <button onClick={() => setCurrent(null)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        </div>
        {message && <div className="p-3 rounded bg-gray-50 text-sm">{message}</div>}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="URL slug" hint="Lives at crystalmedia.ca/<slug>">
            <input data-testid="seo-slug-input" value={current.slug} onChange={(e) => set('slug', e.target.value)} className={inputCls} placeholder="wedding-photographer-toronto" />
          </Field>
          <Field label="Service">
            <select value={current.service} onChange={(e) => set('service', e.target.value)} className={inputCls}>
              <option value="photographer">Photographer</option>
              <option value="videographer">Videographer</option>
            </select>
          </Field>
          <Field label="City (optional)"><input value={current.city} onChange={(e) => set('city', e.target.value)} className={inputCls} /></Field>
          <Field label="Culture (optional)"><input value={current.culture} onChange={(e) => set('culture', e.target.value)} className={inputCls} placeholder="Pakistani, Sikh, Indian…" /></Field>
        </div>

        <Field label="H1 heading"><input data-testid="seo-h1-input" value={current.h1} onChange={(e) => set('h1', e.target.value)} className={inputCls} /></Field>
        <Field label="Meta title" hint="≤ 60 characters is ideal"><input value={current.title} onChange={(e) => set('title', e.target.value)} className={inputCls} /></Field>
        <Field label="Meta description" hint={`${current.meta_description.length}/160`}><textarea rows={2} value={current.meta_description} onChange={(e) => set('meta_description', e.target.value)} className={inputCls} /></Field>
        <Field label="Eyebrow"><input value={current.eyebrow} onChange={(e) => set('eyebrow', e.target.value)} className={inputCls} placeholder="Toronto · Pakistani Weddings · Photography" /></Field>
        <Field label="Intro paragraph"><textarea rows={4} value={current.intro} onChange={(e) => set('intro', e.target.value)} className={inputCls} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Keywords (comma separated)"><input value={current.keywords.join(', ')} onChange={(e) => set('keywords', toList(e.target.value))} className={inputCls} /></Field>
          <Field label="Auto-pick media by tags (comma separated)" hint="Collections/films whose tags match are featured automatically"><input value={current.filter_tags.join(', ')} onChange={(e) => set('filter_tags', toList(e.target.value))} className={inputCls} /></Field>
          <Field label="CTA button text"><input value={current.cta_text} onChange={(e) => set('cta_text', e.target.value)} className={inputCls} /></Field>
          <Field label="Hero / OG image URL (optional)"><input value={current.hero_image_url} onChange={(e) => set('hero_image_url', e.target.value)} className={inputCls} /></Field>
        </div>

        <Field label="Pin specific collections (overrides tag matching)">
          <div className="flex flex-wrap gap-2">
            {collections.map((c) => (
              <button key={c.id} type="button" onClick={() => toggleId('featured_collection_ids', c.id)}
                className={`px-3 py-1 text-xs rounded-full border ${current.featured_collection_ids.includes(c.id) ? 'bg-black text-white border-black' : 'border-gray-300 hover:bg-gray-50'}`}>{c.couple}</button>
            ))}
          </div>
        </Field>
        <Field label="Pin specific films">
          <div className="flex flex-wrap gap-2">
            {films.map((f) => (
              <button key={f.id} type="button" onClick={() => toggleId('featured_film_ids', f.id)}
                className={`px-3 py-1 text-xs rounded-full border ${current.featured_film_ids.includes(f.id) ? 'bg-black text-white border-black' : 'border-gray-300 hover:bg-gray-50'}`}>{f.title}</button>
            ))}
          </div>
        </Field>

        <Field label="Body sections">
          <ListEditor items={current.sections} onChange={(v) => set('sections', v)} fields={[['heading', 'Heading'], ['content', 'Content', true]]} addLabel="+ Add section" testid="seo-add-section" />
        </Field>
        <Field label="FAQs (rendered with FAQ schema)">
          <ListEditor items={current.faqs} onChange={(v) => set('faqs', v)} fields={[['question', 'Question'], ['answer', 'Answer', true]]} addLabel="+ Add FAQ" testid="seo-add-faq" />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={current.is_published} onChange={(e) => set('is_published', e.target.checked)} /> Published (visible + in sitemap)
        </label>

        <button onClick={save} data-testid="seo-save-btn" className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800">Save Page</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seo-pages-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">SEO Landing Pages</h2>
          <p className="text-sm text-gray-500 mt-1">City & culture pages that rank for "wedding photographer toronto"-style searches. Auto-added to the sitemap.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seed} data-testid="seo-seed-btn" className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Seed defaults</button>
          <button onClick={() => setCurrent({ ...EMPTY })} data-testid="seo-new-btn" className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800">+ New page</button>
        </div>
      </div>
      {message && <div className="p-3 rounded bg-gray-50 text-sm">{message}</div>}

      <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
        {pages.map((p) => (
          <div key={p.id} className="p-4 flex items-center justify-between gap-4" data-testid={`seo-page-row-${p.slug}`}>
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">{p.h1}</p>
              <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">/{p.slug}</a>
              {!p.is_published && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-600">draft</span>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setCurrent({ ...EMPTY, ...p })} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Edit</button>
              <button onClick={() => remove(p.id)} className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50">Delete</button>
            </div>
          </div>
        ))}
        {pages.length === 0 && <p className="p-8 text-center text-gray-500 text-sm">No pages yet — click "Seed defaults" to create 12 starter pages.</p>}
      </div>
    </div>
  );
};

export default SeoPagesPanel;
